// Shared money formatting for the storefront. Designed so Israel (ILS) renders
// EXACTLY as before (₪, caller-controlled decimals) while other currencies show
// their symbol/code — Jordan shows JOD (3 decimals). Replaces the per-component
// hardcoded ₪ + duplicated currencySymbol() helpers.

const SYMBOLS = { ILS: '₪', USD: '$', EUR: '€', GBP: '£' };
// Arabic currency symbols — shown when the storefront UI language is Arabic so a
// Jordanian shop renders "د.أ" instead of the ISO code "JOD". Mirrors the portal.
const SYMBOLS_AR = {
  ILS: '₪', USD: '$', EUR: '€', GBP: '£',
  JOD: 'د.أ', SAR: 'ر.س', AED: 'د.إ', EGP: 'ج.م', KWD: 'د.ك', QAR: 'ر.ق',
  BHD: 'د.ب', OMR: 'ر.ع', LBP: 'ل.ل', IQD: 'د.ع', SYP: 'ل.س', YER: 'ر.ي',
};
// Currencies with 3 minor units (JOD/KWD/BHD/OMR/TND/LYD).
const THREE_DECIMAL = new Set(['JOD', 'KWD', 'BHD', 'OMR', 'TND', 'LYD']);

// Current storefront UI language — App sets <html lang>; no i18n import needed.
const currentLang = () => {
  try {
    return (typeof document !== 'undefined'
      ? (document.documentElement.getAttribute('lang') || '')
      : '');
  } catch (_) {
    return '';
  }
};

export const currencySymbol = (currency, locale) => {
  const code = String(currency || 'ILS').toUpperCase();
  const lang = String(locale != null ? locale : currentLang()).toLowerCase();
  if (lang.startsWith('ar') && SYMBOLS_AR[code]) return SYMBOLS_AR[code];
  return SYMBOLS[code] || code;
};

export const currencyDecimals = (currency) =>
  THREE_DECIMAL.has(String(currency || '').toUpperCase()) ? 3 : 2;

/**
 * A shop price's NUMBER, as the shopper should read it: whole units when the
 * amount is whole ("579"), the currency's own decimals when it is not
 * ("492.15", "2.500"). Never rounded to whole units — a 15% sale charges
 * 492.15, and a page that prints 492 is showing one number and taking
 * another. See `utils/__tests__/aPriceIsNeverRoundedAway.test.js`.
 */
export const shopAmount = (amount, currency = 'ILS') => {
  const dec = currencyDecimals(currency);
  const factor = 10 ** dec;
  const n = Number(amount);
  const units = Number.isFinite(n) ? Math.round(n * factor) : 0;
  if (units === 0) return '0';
  return units % factor === 0 ? String(units / factor) : (units / factor).toFixed(dec);
};

/**
 * `shopAmount` with its whole part grouped in thousands — "1,800", "1,020.50"
 * — for the surfaces that set a large price in display type. The same number,
 * never rounded; only the separator is added.
 */
export const groupedShopAmount = (amount, currency = 'ILS') => {
  const text = shopAmount(amount, currency);
  const negative = text.startsWith('-');
  const [whole, fraction] = (negative ? text.slice(1) : text).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${negative ? '-' : ''}${grouped}${fraction ? `.${fraction}` : ''}`;
};

/** `shopAmount` with the currency symbol after it: "492.15 ₪". */
export const shopPrice = (amount, currency = 'ILS', opts = {}) =>
  `${shopAmount(amount, currency)} ${currencySymbol(currency, opts.locale)}`;

/**
 * Format an amount for display. By default: symbol prefix + currency-correct
 * decimals (ILS/USD/EUR=2, JOD=3). Pass { decimals } to match a specific site's
 * existing format (so ILS displays stay pixel-identical), and { position:'suffix' }
 * for "80 ₪" style.
 */
export const formatMoney = (amount, currency = 'ILS', opts = {}) => {
  const dec = opts.decimals != null ? opts.decimals : currencyDecimals(currency);
  const n = (Number(amount) || 0).toFixed(dec);
  // `locale` is passed through to `currencySymbol`, which picks the Arabic
  // form of a symbol where one exists. Without it the symbol was resolved
  // against whatever `currentLang()` happened to be — the reader's i18n
  // state — rather than against the language the caller is rendering in,
  // which is not always the same thing (a server-rendered string, a preview,
  // a test).
  const sym = currencySymbol(currency, opts.locale);
  return opts.position === 'suffix' ? `${n} ${sym}` : `${sym}${n}`;
};
